import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, ShieldCheckIcon, ChevronDownIcon, StarIcon, LockIcon, ZapIcon, FileTextIcon, BrainIcon, BookOpenIcon, PlayIcon } from 'lucide-react';
import { CountdownTimer } from './CountdownTimer';
import { SocialProofPopup } from './SocialProofPopup';
import { PricingGrid } from './PricingGrid';
const IMAGES = {
  MAIN: 'https://cdn.magicpatterns.com/uploads/rkmU6qCqur8ByocWsgGsBq/MAESTRO_CERRAJERO.png'
};
const LINKS = {
  PACK_COMPLETO: 'https://mi-tienda-pro-8012.myshopify.com/cart/48504331469019:1',
  PACK_PREMIUM: 'https://mi-tienda-pro-8012.myshopify.com/cart/48504331895003:1'
};
export function SalesPage() {
  const [openModule, setOpenModule] = useState<number | null>(null);
  const [selectedPack, setSelectedPack] = useState(1); // Default to premium
  const packLinks = [LINKS.PACK_COMPLETO, LINKS.PACK_PREMIUM];
  const [urgencyTime, setUrgencyTime] = useState({
    hours: 2,
    minutes: 13,
    seconds: 30
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setUrgencyTime(prev => {
        let {
          hours,
          minutes,
          seconds
        } = prev;
        seconds--;
        if (seconds < 0) {
          seconds = 59;
          minutes--;
        }
        if (minutes < 0) {
          minutes = 59;
          hours--;
        }
        if (hours < 0) {
          hours = 0;
          minutes = 0;
          seconds = 0;
        }
        return {
          hours,
          minutes,
          seconds
        };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const scrollToPricing = () => {
    const element = document.getElementById('pricing');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  // Animation variants
  const fadeUpVariants = {
    hidden: {
      opacity: 0,
      y: 24
    },
    visible: (custom: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: custom * 0.1,
        duration: 0.6,
        ease: 'easeOut'
      }
    })
  };
  return <div className="font-['Barlow'] text-[#e8e0d0] bg-[#0a0a0a] min-h-screen flex flex-col selection:bg-[#f5a623] selection:text-[#0a0a0a]" data-id="element-35">
      <CountdownTimer data-id="element-36" />
      <SocialProofPopup data-id="element-37" />

      {/* 1. HERO SECTION */}
      <section className="bg-[#111111] pt-12 pb-10 md:pt-[70px] md:pb-[60px] px-5 text-center relative overflow-hidden border-b border-[#2a2a2a]" data-id="element-38">
        <div className="absolute -top-[200px] left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[radial-gradient(circle,rgba(245,166,35,0.12)_0%,transparent_70%)] pointer-events-none" data-id="element-39"></div>

        <div className="max-w-[780px] mx-auto flex flex-col items-center relative z-10" data-id="element-40">
          <motion.div custom={1} initial="hidden" animate="visible" variants={fadeUpVariants} className="inline-block bg-[rgba(245,166,35,0.15)] border border-[rgba(245,166,35,0.4)] text-[#f5a623] text-[12px] font-bold tracking-[0.15em] uppercase py-1.5 px-4 rounded-sm mb-7" data-id="element-41">
            ⭐ EL RECURSO #1 PARA GENERAR INGRESOS CON CERRAJERÍA ⭐
          </motion.div>

          <motion.h1 custom={2} initial="hidden" animate="visible" variants={fadeUpVariants} className="font-['Bebas_Neue'] text-[clamp(48px,8vw,80px)] leading-[0.95] text-[#f5f0e8] tracking-[0.02em] mb-2.5 uppercase" data-id="element-42">
            Aprendé una habilidad real y empezá a ganar dinero abriendo puertas,
            cambiando cerraduras y resolviendo urgencias
          </motion.h1>

          <motion.p custom={3} initial="hidden" animate="visible" variants={fadeUpVariants} className="text-[clamp(18px,3vw,22px)] font-medium text-[#bbb] max-w-[600px] mx-auto mt-5 leading-[1.5]" data-id="element-43">
            <strong data-id="element-44">aunque no tengas experiencia</strong>
          </motion.p>

          <motion.div custom={4} initial="hidden" animate="visible" variants={fadeUpVariants} onClick={scrollToPricing} className="cursor-pointer hover:-translate-y-1 transition-transform duration-300 my-8 w-full max-w-lg relative group" data-id="element-45">
            <div className="absolute inset-0 bg-[#f5a623] opacity-10 blur-2xl rounded-full group-hover:opacity-20 transition-opacity" data-id="element-46"></div>
            <img src={IMAGES.MAIN} alt="Mega Pack Maestro Cerrajero" className="w-full relative z-10 drop-shadow-[0_0_25px_rgba(245,166,35,0.2)]" data-id="element-47" />
          </motion.div>

          <motion.p custom={5} initial="hidden" animate="visible" variants={fadeUpVariants} className="text-[13px] text-[#888888] italic mt-3.5" data-id="element-48">
            TODO EN UN SOLO LUGAR. LISTO PARA USAR. SIN CONOCIMIENTOS PREVIOS.
          </motion.p>

          <motion.p custom={6} initial="hidden" animate="visible" variants={fadeUpVariants} className="text-[15px] text-[#888888] mb-6 max-w-xl mx-auto leading-snug" data-id="element-49">
            El sistema que están utilizando personas comunes para aprender
            cerrajería desde cero y comenzar a trabajar en pocas semanas
          </motion.p>

          <motion.button custom={7} initial="hidden" animate="visible" variants={fadeUpVariants} onClick={scrollToPricing} className="btn-primary inline-block bg-[#f5a623] text-[#0a0a0a] font-['Barlow_Condensed'] font-extrabold text-[20px] tracking-[0.05em] uppercase py-[18px] px-[48px] rounded-[4px] mt-[10px] border-none cursor-pointer" data-id="element-50">
            → QUIERO ACCEDER AHORA
          </motion.button>

          <motion.div custom={8} initial="hidden" animate="visible" variants={fadeUpVariants} className="flex justify-center gap-7 mt-6 flex-wrap" data-id="element-51">
            <div className="flex items-center gap-1.5 text-[13px] text-[#888888] font-semibold uppercase tracking-[0.05em]" data-id="element-52">
              <span className="text-[#22c55e] text-[16px]" data-id="element-53">✓</span> Garantía 7
              días
            </div>
            <div className="flex items-center gap-1.5 text-[13px] text-[#888888] font-semibold uppercase tracking-[0.05em]" data-id="element-54">
              <span className="text-[#22c55e] text-[16px]" data-id="element-55">✓</span> Pago seguro
            </div>
            <div className="flex items-center gap-1.5 text-[13px] text-[#888888] font-semibold uppercase tracking-[0.05em]" data-id="element-56">
              <span className="text-[#22c55e] text-[16px]" data-id="element-57">✓</span> Acceso
              inmediato
            </div>
          </motion.div>
        </div>
      </section>

      <hr className="border-none border-t border-[#2a2a2a] m-0" data-id="element-58" />

      {/* 2. PAIN POINTS */}
      <section className="py-10 md:py-16 px-5 bg-[#111111] border-y border-[#2a2a2a]" data-id="element-59">
        <div className="max-w-[780px] mx-auto" data-id="element-60">
          <div className="font-['Barlow_Condensed'] text-[12px] font-bold tracking-[0.2em] uppercase text-[#f5a623] mb-4" data-id="element-61">
            ¿Te identificás con esto?
          </div>
          <h2 className="font-['Bebas_Neue'] text-[clamp(36px,6vw,56px)] leading-none text-[#f5f0e8] tracking-[0.02em] mb-6 uppercase" data-id="element-62">
            ¿TE <span className="text-[#f5a623]" data-id="element-63">PASA</span> ESTO?
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8" data-id="element-64">
            <div className="bg-[rgba(239,68,68,0.06)] border border-[rgba(239,68,68,0.2)] rounded-md p-5 flex items-start gap-3" data-id="element-65">
              <div className="text-[20px] flex-shrink-0 mt-0.5" data-id="element-66">❌</div>
              <div className="text-[15px] text-[#ccc] leading-[1.4]" data-id="element-67">
                Querés generar ingresos pero no sabés por dónde empezar
              </div>
            </div>
            <div className="bg-[rgba(239,68,68,0.06)] border border-[rgba(239,68,68,0.2)] rounded-md p-5 flex items-start gap-3" data-id="element-68">
              <div className="text-[20px] flex-shrink-0 mt-0.5" data-id="element-69">❌</div>
              <div className="text-[15px] text-[#ccc] leading-[1.4]" data-id="element-70">
                No te alcanza el dinero y necesitás una solución urgente
              </div>
            </div>
            <div className="bg-[rgba(239,68,68,0.06)] border border-[rgba(239,68,68,0.2)] rounded-md p-5 flex items-start gap-3" data-id="element-71">
              <div className="text-[20px] flex-shrink-0 mt-0.5" data-id="element-72">❌</div>
              <div className="text-[15px] text-[#ccc] leading-[1.4]" data-id="element-73">
                Dependés de un trabajo que no te permite crecer
              </div>
            </div>
            <div className="bg-[rgba(239,68,68,0.06)] border border-[rgba(239,68,68,0.2)] rounded-md p-5 flex items-start gap-3" data-id="element-74">
              <div className="text-[20px] flex-shrink-0 mt-0.5" data-id="element-75">❌</div>
              <div className="text-[15px] text-[#ccc] leading-[1.4]" data-id="element-76">
                No tenés una habilidad concreta para ofrecer a otros
              </div>
            </div>
            <div className="bg-[rgba(239,68,68,0.06)] border border-[rgba(239,68,68,0.2)] rounded-md p-5 flex items-start gap-3" data-id="element-77">
              <div className="text-[20px] flex-shrink-0 mt-0.5" data-id="element-78">❌</div>
              <div className="text-[15px] text-[#ccc] leading-[1.4]" data-id="element-79">
                Sentís que el tiempo pasa y seguís en el mismo lugar
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. REALITY SECTION */}
      <section className="py-10 md:py-16 px-5 bg-[#0a0a0a]" data-id="element-80">
        <div className="max-w-[780px] mx-auto" data-id="element-81">
          <div className="font-['Barlow_Condensed'] text-[12px] font-bold tracking-[0.2em] uppercase text-[#f5a623] mb-4" data-id="element-82">
            La oportunidad que pocos ven
          </div>
          <h2 className="font-['Bebas_Neue'] text-[clamp(36px,6vw,56px)] leading-none text-[#f5f0e8] tracking-[0.02em] mb-6 uppercase" data-id="element-83">
            LA <span className="text-[#f5a623]" data-id="element-84">REALIDAD</span> QUE NADIE TE
            CUENTA
          </h2>

          <div className="bg-[linear-gradient(135deg,rgba(245,166,35,0.08),rgba(245,166,35,0.03))] border border-[rgba(245,166,35,0.25)] rounded-lg p-8 mt-8" data-id="element-85">
            <p className="text-[18px] text-[#ccc] mb-5" data-id="element-86">
              Hoy hay personas que están generando ingresos con trabajos simples
              como:
            </p>
            <ul className="list-none" data-id="element-87">
              <li className="py-3 border-b border-[#2a2a2a] flex items-center gap-3 text-[16px] text-[#e8e0d0]" data-id="element-88">
                <span className="text-[#f5a623] font-bold flex-shrink-0" data-id="element-89">
                  →
                </span>{' '}
                Apertura de puertas trabadas
              </li>
              <li className="py-3 border-b border-[#2a2a2a] flex items-center gap-3 text-[16px] text-[#e8e0d0]" data-id="element-90">
                <span className="text-[#f5a623] font-bold flex-shrink-0" data-id="element-91">
                  →
                </span>{' '}
                Cambio de cerraduras
              </li>
              <li className="py-3 flex items-center gap-3 text-[16px] text-[#e8e0d0]" data-id="element-92">
                <span className="text-[#f5a623] font-bold flex-shrink-0" data-id="element-93">
                  →
                </span>{' '}
                Resolución de urgencias básicas
              </li>
            </ul>
            <br data-id="element-94" />
            <p className="text-[#aaa]" data-id="element-95">
              Y lo hacen con herramientas accesibles y conocimiento práctico.{' '}
              <strong className="text-[#f5f0e8]" data-id="element-96">
                Vos también podés hacerlo.
              </strong>
            </p>
          </div>
        </div>
      </section>

      {/* 4. WARNING SECTION */}
      <section className="py-10 md:py-16 px-5 bg-[#111111] border-y border-[#2a2a2a]" data-id="element-97">
        <div className="max-w-[780px] mx-auto" data-id="element-98">
          <div className="font-['Barlow_Condensed'] text-[12px] font-bold tracking-[0.2em] uppercase text-[#f5a623] mb-4" data-id="element-99">
            El obstáculo principal
          </div>
          <h2 className="font-['Bebas_Neue'] text-[clamp(36px,6vw,56px)] leading-none text-[#f5f0e8] tracking-[0.02em] mb-6 uppercase" data-id="element-100">
            ⚠️ VOS TAMBIÉN <span className="text-[#f5a623]" data-id="element-101">PODÉS HACERLO</span>
          </h2>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-8" data-id="element-102">
            <p className="text-[18px] text-[#ccc] mb-5" data-id="element-103">
              Pero no lo estás haciendo porque:
            </p>
            <ul className="list-none" data-id="element-104">
              <li className="py-3 border-b border-[#2a2a2a] flex items-center gap-3 text-[16px] text-[#e8e0d0]" data-id="element-105">
                <span className="text-[#ef4444] font-bold flex-shrink-0" data-id="element-106">
                  →
                </span>{' '}
                No sabés por dónde empezar
              </li>
              <li className="py-3 border-b border-[#2a2a2a] flex items-center gap-3 text-[16px] text-[#e8e0d0]" data-id="element-107">
                <span className="text-[#ef4444] font-bold flex-shrink-0" data-id="element-108">
                  →
                </span>{' '}
                No tenés una guía clara
              </li>
              <li className="py-3 flex items-center gap-3 text-[16px] text-[#e8e0d0]" data-id="element-109">
                <span className="text-[#ef4444] font-bold flex-shrink-0" data-id="element-110">
                  →
                </span>{' '}
                Todo lo que encontrás está desordenado
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* 5. EXTRA CTA */}
      <section className="py-12 md:py-20 px-5 bg-[#0a0a0a] text-center" data-id="element-111">
        <div className="max-w-[780px] mx-auto" data-id="element-112">
          <button onClick={scrollToPricing} className="btn-primary w-full max-w-2xl bg-[#f5a623] text-[#0a0a0a] font-['Barlow_Condensed'] font-extrabold text-[18px] md:text-[20px] tracking-[0.05em] uppercase py-[18px] px-[24px] rounded-[4px] border-none cursor-pointer" data-id="element-113">
            🔥 ¡QUIERO DEJAR DE PERDER TIEMPO Y EMPEZAR A GENERAR INGRESOS!
          </button>
        </div>
      </section>

      {/* 6. SOLUTION SECTION */}
      <section className="py-10 md:py-16 px-5 bg-[#111111] border-y border-[#2a2a2a]" data-id="element-114">
        <div className="max-w-[780px] mx-auto" data-id="element-115">
          <div className="font-['Barlow_Condensed'] text-[12px] font-bold tracking-[0.2em] uppercase text-[#f5a623] mb-4" data-id="element-116">
            La solución definitiva
          </div>
          <h2 className="font-['Bebas_Neue'] text-[clamp(36px,6vw,56px)] leading-none text-[#f5f0e8] tracking-[0.02em] mb-6 uppercase" data-id="element-117">
            🚀 CON ESTE SISTEMA{' '}
            <span className="text-[#f5a623]" data-id="element-118">VAS A PODER:</span>
          </h2>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-8" data-id="element-119">
            <ul className="list-none" data-id="element-120">
              <li className="py-3 border-b border-[#2a2a2a] flex items-center gap-3 text-[16px] text-[#e8e0d0]" data-id="element-121">
                <span className="text-[#22c55e] font-bold flex-shrink-0" data-id="element-122">
                  ✓
                </span>{' '}
                Aprender cerrajería desde cero, paso a paso
              </li>
              <li className="py-3 border-b border-[#2a2a2a] flex items-center gap-3 text-[16px] text-[#e8e0d0]" data-id="element-123">
                <span className="text-[#22c55e] font-bold flex-shrink-0" data-id="element-124">
                  ✓
                </span>{' '}
                Abrir puertas sin dañar las cerraduras
              </li>
              <li className="py-3 border-b border-[#2a2a2a] flex items-center gap-3 text-[16px] text-[#e8e0d0]" data-id="element-125">
                <span className="text-[#22c55e] font-bold flex-shrink-0" data-id="element-126">
                  ✓
                </span>{' '}
                Cambiar cerraduras completas
              </li>
              <li className="py-3 border-b border-[#2a2a2a] flex items-center gap-3 text-[16px] text-[#e8e0d0]" data-id="element-127">
                <span className="text-[#22c55e] font-bold flex-shrink-0" data-id="element-128">
                  ✓
                </span>{' '}
                Entender cómo funcionan los sistemas
              </li>
              <li className="py-3 flex items-center gap-3 text-[16px] text-[#e8e0d0]" data-id="element-129">
                <span className="text-[#22c55e] font-bold flex-shrink-0" data-id="element-130">
                  ✓
                </span>{' '}
                Ofrecer servicios pagos desde el inicio
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* 7. TIMELINE SECTION */}
      <section className="py-10 md:py-16 px-5 bg-[#0a0a0a]" data-id="element-131">
        <div className="max-w-[780px] mx-auto" data-id="element-132">
          <div className="font-['Barlow_Condensed'] text-[12px] font-bold tracking-[0.2em] uppercase text-[#f5a623] mb-4" data-id="element-133">
            Tu progreso semana a semana
          </div>
          <h2 className="font-['Bebas_Neue'] text-[clamp(36px,6vw,56px)] leading-none text-[#f5f0e8] tracking-[0.02em] mb-6 uppercase" data-id="element-134">
            ⏱ ¿EN CUÁNTO <span className="text-[#f5a623]" data-id="element-135">TIEMPO?</span>
          </h2>

          <div className="mt-9" data-id="element-136">
            <div className="flex gap-6 mb-8 relative before:content-[''] before:absolute before:left-[19px] before:top-[44px] before:bottom-[-32px] before:w-[2px] before:bg-gradient-to-b before:from-[#c47d0e] before:to-transparent" data-id="element-137">
              <div className="w-10 h-10 rounded-full bg-[#f5a623] text-[#0a0a0a] font-['Bebas_Neue'] text-[20px] flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_rgba(245,166,35,0.3)]" data-id="element-138">
                1
              </div>
              <div data-id="element-139">
                <h3 className="font-['Barlow_Condensed'] text-[20px] font-bold text-[#f5f0e8] mb-1 uppercase tracking-[0.05em]" data-id="element-140">
                  En pocos días
                </h3>
                <p className="text-[15px] text-[#aaa]" data-id="element-141">
                  Vas a entender cómo funciona todo el sistema y la teoría base.
                </p>
              </div>
            </div>
            <div className="flex gap-6 mb-8 relative before:content-[''] before:absolute before:left-[19px] before:top-[44px] before:bottom-[-32px] before:w-[2px] before:bg-gradient-to-b before:from-[#c47d0e] before:to-transparent" data-id="element-142">
              <div className="w-10 h-10 rounded-full bg-[#f5a623] text-[#0a0a0a] font-['Bebas_Neue'] text-[20px] flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_rgba(245,166,35,0.3)]" data-id="element-143">
                2
              </div>
              <div data-id="element-144">
                <h3 className="font-['Barlow_Condensed'] text-[20px] font-bold text-[#f5f0e8] mb-1 uppercase tracking-[0.05em]" data-id="element-145">
                  En 1 a 2 semanas
                </h3>
                <p className="text-[15px] text-[#aaa]" data-id="element-146">
                  Podés comenzar a practicar con las herramientas adecuadas.
                </p>
              </div>
            </div>
            <div className="flex gap-6 relative" data-id="element-147">
              <div className="w-10 h-10 rounded-full bg-[#f5a623] text-[#0a0a0a] font-['Bebas_Neue'] text-[20px] flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_rgba(245,166,35,0.3)]" data-id="element-148">
                3
              </div>
              <div data-id="element-149">
                <h3 className="font-['Barlow_Condensed'] text-[20px] font-bold text-[#f5f0e8] mb-1 uppercase tracking-[0.05em]" data-id="element-150">
                  En pocas semanas
                </h3>
                <p className="text-[15px] text-[#aaa]" data-id="element-151">
                  Podés realizar tus primeros trabajos reales y cobrar por
                  ellos.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. EARNINGS SECTION */}
      <section className="py-10 md:py-16 px-5 bg-[#111111] border-y border-[#2a2a2a]" data-id="element-152">
        <div className="max-w-[780px] mx-auto" data-id="element-153">
          <div className="font-['Barlow_Condensed'] text-[12px] font-bold tracking-[0.2em] uppercase text-[#f5a623] mb-4" data-id="element-154">
            Lo que el mercado paga hoy
          </div>
          <h2 className="font-['Bebas_Neue'] text-[clamp(36px,6vw,56px)] leading-none text-[#f5f0e8] tracking-[0.02em] mb-6 uppercase" data-id="element-155">
            💰 ¿CUÁNTO PODÉS <span className="text-[#f5a623]" data-id="element-156">GENERAR?</span>
          </h2>

          <p className="text-[18px] text-[#ccc] mb-8" data-id="element-157">
            Un solo trabajo puede pagarse entre:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8" data-id="element-158">
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-6 text-center" data-id="element-159">
              <div className="font-['Bebas_Neue'] text-[42px] text-[#f5a623] tracking-[0.02em] leading-none" data-id="element-160">
                $10.000 a $30.000
              </div>
              <div className="text-[13px] text-[#888] mt-1.5 leading-[1.4]" data-id="element-161">
                Apertura o cambio simple
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-[rgba(34,197,94,0.07)] border border-[rgba(34,197,94,0.2)] rounded-md text-[14px] text-[#aaa]" data-id="element-162">
            <strong className="text-[#22c55e]" data-id="element-163">
              Con 2 o 3 trabajos podés recuperar la inversión.
            </strong>{' '}
            Todo lo demás es ganancia neta.
          </div>
        </div>
      </section>

      {/* 9. PRODUCT PRESENTATION */}
      <section className="py-10 md:py-16 px-5 bg-[#0a0a0a] text-center" data-id="element-164">
        <div className="max-w-[780px] mx-auto" data-id="element-165">
          <div className="inline-block bg-[#ef4444] text-[#f5f0e8] text-[12px] font-bold px-3 py-1 rounded-sm mb-4 uppercase tracking-[0.1em]" data-id="element-166">
            🔥 PRESENTAMOS:
          </div>
          <h2 className="font-['Bebas_Neue'] text-[clamp(40px,7vw,64px)] leading-none text-[#f5f0e8] tracking-[0.02em] mb-4 uppercase" data-id="element-167">
            💣 MEGA PACK{' '}
            <span className="text-[#f5a623]" data-id="element-168">MAESTRO CERRAJERO</span>
          </h2>
          <p className="text-[18px] text-[#aaa] max-w-[560px] mx-auto" data-id="element-169">
            Un sistema completo en PDF + videos, explicado de forma clara,
            simple y directa, diseñado para que cualquier persona pueda aprender
            desde cero.
          </p>
        </div>
      </section>

      {/* 10. CONTENTS SECTION */}
      <section className="py-10 md:py-16 px-5 bg-[#111111] border-y border-[#2a2a2a]" data-id="element-170">
        <div className="max-w-[780px] mx-auto" data-id="element-171">
          <div className="text-center mb-8" data-id="element-172">
            <div className="font-['Barlow_Condensed'] text-[12px] font-bold tracking-[0.2em] uppercase text-[#f5a623] mb-4" data-id="element-173">
              Lo que recibís
            </div>
            <h2 className="font-['Bebas_Neue'] text-[clamp(36px,6vw,56px)] leading-none text-[#f5f0e8] tracking-[0.02em] mb-6 uppercase" data-id="element-174">
              📦 ¿QUÉ <span className="text-[#f5a623]" data-id="element-175">INCLUYE?</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 md:gap-5 mb-10" data-id="element-176">
            {/* Module 1 */}
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-5 md:p-7 relative overflow-hidden transition-colors duration-200 hover:border-[rgba(245,166,35,0.4)] before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-[3px] before:bg-gradient-to-r before:from-[#c47d0e] before:to-[#f5a623]" data-id="element-177">
              <div className="text-[32px] mb-3.5" data-id="element-178">🔓</div>
              <div className="font-['Barlow_Condensed'] text-[18px] font-bold text-[#f5f0e8] uppercase tracking-[0.05em] mb-2" data-id="element-179">
                CERRAJERÍA DE PUERTAS (BASE COMPLETA)
              </div>
              <ul className="text-[14px] text-[#888] leading-[1.5] list-none space-y-1" data-id="element-180">
                <li data-id="element-181">• Funcionamiento de las cerraduras</li>
                <li data-id="element-182">• Tipos de cerraduras más comunes</li>
                <li data-id="element-183">• Técnicas de apertura sin daño</li>
                <li data-id="element-184">• Uso correcto de ganzúas</li>
                <li data-id="element-185">• Métodos reales utilizados en el trabajo</li>
                <li data-id="element-186">• Resolución de problemas frecuentes</li>
              </ul>
            </div>

            {/* Module 2 */}
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-5 md:p-7 relative overflow-hidden transition-colors duration-200 hover:border-[rgba(245,166,35,0.4)] before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-[3px] before:bg-gradient-to-r before:from-[#c47d0e] before:to-[#f5a623]" data-id="element-187">
              <div className="text-[32px] mb-3.5" data-id="element-188">🧠</div>
              <div className="font-['Barlow_Condensed'] text-[18px] font-bold text-[#f5f0e8] uppercase tracking-[0.05em] mb-2" data-id="element-189">
                TÉCNICAS DE LOCKPICKING (GANZÚAS)
              </div>
              <ul className="text-[14px] text-[#888] leading-[1.5] list-none space-y-1" data-id="element-190">
                <li data-id="element-191">• Uso correcto de herramientas</li>
                <li data-id="element-192">• Técnicas básicas y avanzadas</li>
                <li data-id="element-193">• Práctica guiada</li>
                <li data-id="element-194">• Errores comunes y cómo evitarlos</li>
              </ul>
            </div>

            {/* Module 3 */}
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-5 md:p-7 relative overflow-hidden transition-colors duration-200 hover:border-[rgba(245,166,35,0.4)] before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-[3px] before:bg-gradient-to-r before:from-[#c47d0e] before:to-[#f5a623]" data-id="element-195">
              <div className="text-[32px] mb-3.5" data-id="element-196">📘</div>
              <div className="font-['Barlow_Condensed'] text-[18px] font-bold text-[#f5f0e8] uppercase tracking-[0.05em] mb-2" data-id="element-197">
                MANUALES PROFESIONALES (PDF)
              </div>
              <ul className="text-[14px] text-[#888] leading-[1.5] list-none space-y-1" data-id="element-198">
                <li data-id="element-199">• Información organizada y práctica</li>
                <li data-id="element-200">• Explicaciones claras</li>
                <li data-id="element-201">• Contenido directo, sin relleno</li>
              </ul>
            </div>

            {/* Module 4 */}
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-5 md:p-7 relative overflow-hidden transition-colors duration-200 hover:border-[rgba(245,166,35,0.4)] before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-[3px] before:bg-gradient-to-r before:from-[#c47d0e] before:to-[#f5a623]" data-id="element-202">
              <div className="text-[32px] mb-3.5" data-id="element-203">🎥</div>
              <div className="font-['Barlow_Condensed'] text-[18px] font-bold text-[#f5f0e8] uppercase tracking-[0.05em] mb-2" data-id="element-204">
                VIDEOS PASO A PASO
              </div>
              <ul className="text-[14px] text-[#888] leading-[1.5] list-none space-y-1" data-id="element-205">
                <li data-id="element-206">• Explicaciones simples</li>
                <li data-id="element-207">• Aplicación real</li>
                <li data-id="element-208">• Aprendizaje visual</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 11. BONUSES SECTION */}
      <section className="py-10 md:py-16 px-5 bg-[#0a0a0a]" data-id="element-209">
        <div className="max-w-[780px] mx-auto" data-id="element-210">
          <div className="text-center mb-6 md:mb-9" data-id="element-211">
            <div className="font-['Barlow_Condensed'] text-[12px] font-bold tracking-[0.2em] uppercase text-[#f5a623] mb-4" data-id="element-212">
              Incluido en tu compra
            </div>
            <h2 className="font-['Bebas_Neue'] text-[clamp(36px,6vw,56px)] leading-none text-[#f5f0e8] tracking-[0.02em] mb-2 uppercase" data-id="element-213">
              🎁 ADEMÁS HOY TE LLEVÁS{' '}
              <span className="text-[#f5a623]" data-id="element-214">GRATIS:</span>
            </h2>
            <div className="inline-block bg-[#ef4444] text-[#f5f0e8] text-[12px] font-bold px-3 py-1 rounded-sm mt-2 uppercase tracking-[0.1em]" data-id="element-215">
              ¡GRATIS POR EL DÍA DE HOY!
            </div>
          </div>

          <div className="flex flex-col gap-4" data-id="element-216">
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-6 md:p-7 flex flex-col md:flex-row justify-between md:items-center gap-5 transition-colors duration-200 hover:border-[rgba(245,166,35,0.3)]" data-id="element-217">
              <div className="flex-1" data-id="element-218">
                <div className="text-[11px] font-bold tracking-[0.15em] uppercase text-[#f5a623] mb-1.5" data-id="element-219">
                  Bonus #1
                </div>
                <div className="font-['Barlow_Condensed'] text-[20px] font-bold text-[#f5f0e8] uppercase tracking-[0.04em] mb-1.5" data-id="element-220">
                  🧰 Maletín Inteligente del Cerrajero
                </div>
                <div className="text-[14px] text-[#888] leading-[1.4]" data-id="element-221">
                  Qué comprar, qué evitar y cómo empezar sin gastar de más.
                </div>
              </div>
              <div className="md:text-right flex-shrink-0" data-id="element-222">
                <div className="text-[11px] text-[#888] uppercase tracking-[0.1em] mb-0.5" data-id="element-223">
                  Valor
                </div>
                <div className="font-['Bebas_Neue'] text-[28px] text-[#555] tracking-[0.04em] line-through leading-none mb-1" data-id="element-224">
                  $8.500
                </div>
                <div className="font-['Bebas_Neue'] text-[28px] text-[#22c55e] tracking-[0.04em] leading-none" data-id="element-225">
                  GRATIS
                </div>
              </div>
            </div>

            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-6 md:p-7 flex flex-col md:flex-row justify-between md:items-center gap-5 transition-colors duration-200 hover:border-[rgba(245,166,35,0.3)]" data-id="element-226">
              <div className="flex-1" data-id="element-227">
                <div className="text-[11px] font-bold tracking-[0.15em] uppercase text-[#f5a623] mb-1.5" data-id="element-228">
                  Bonus #2
                </div>
                <div className="font-['Barlow_Condensed'] text-[20px] font-bold text-[#f5f0e8] uppercase tracking-[0.04em] mb-1.5" data-id="element-229">
                  🔓 Método Ganzúa PRO
                </div>
                <div className="text-[14px] text-[#888] leading-[1.4]" data-id="element-230">
                  Cómo abrir cerraduras paso a paso como un profesional.
                </div>
              </div>
              <div className="md:text-right flex-shrink-0" data-id="element-231">
                <div className="text-[11px] text-[#888] uppercase tracking-[0.1em] mb-0.5" data-id="element-232">
                  Valor
                </div>
                <div className="font-['Bebas_Neue'] text-[28px] text-[#555] tracking-[0.04em] line-through leading-none mb-1" data-id="element-233">
                  $8.500
                </div>
                <div className="font-['Bebas_Neue'] text-[28px] text-[#22c55e] tracking-[0.04em] leading-none" data-id="element-234">
                  GRATIS
                </div>
              </div>
            </div>

            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-6 md:p-7 flex flex-col md:flex-row justify-between md:items-center gap-5 transition-colors duration-200 hover:border-[rgba(245,166,35,0.3)]" data-id="element-235">
              <div className="flex-1" data-id="element-236">
                <div className="text-[11px] font-bold tracking-[0.15em] uppercase text-[#f5a623] mb-1.5" data-id="element-237">
                  Bonus #3
                </div>
                <div className="font-['Barlow_Condensed'] text-[20px] font-bold text-[#f5f0e8] uppercase tracking-[0.04em] mb-1.5" data-id="element-238">
                  📖 Biblia del Cerrajero
                </div>
                <div className="text-[14px] text-[#888] leading-[1.4]" data-id="element-239">
                  Todo lo que necesitás saber para trabajar de verdad.
                </div>
              </div>
              <div className="md:text-right flex-shrink-0" data-id="element-240">
                <div className="text-[11px] text-[#888] uppercase tracking-[0.1em] mb-0.5" data-id="element-241">
                  Valor
                </div>
                <div className="font-['Bebas_Neue'] text-[28px] text-[#555] tracking-[0.04em] line-through leading-none mb-1" data-id="element-242">
                  $9.000
                </div>
                <div className="font-['Bebas_Neue'] text-[28px] text-[#22c55e] tracking-[0.04em] leading-none" data-id="element-243">
                  GRATIS
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-[rgba(34,197,94,0.07)] border border-[rgba(34,197,94,0.2)] rounded-lg p-5 md:p-7 flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left" data-id="element-244">
            <div className="font-['Barlow_Condensed'] text-[16px] font-bold uppercase tracking-[0.05em] text-[#aaa]" data-id="element-245">
              Valor total de los bonus:
            </div>
            <div className="font-['Bebas_Neue'] text-[32px] text-[#22c55e] tracking-[0.04em] leading-none" data-id="element-246">
              $26.000 → INCLUIDOS GRATIS
            </div>
          </div>
        </div>
      </section>

      {/* 12. URGENCY SECTION */}

      {/* 13. PRICING SECTION */}
      <section id="pricing" className="py-10 md:py-16 px-5 bg-[#0a0a0a]" data-id="element-247">
        <div className="max-w-[780px] mx-auto" data-id="element-248">
          <div className="text-center mb-8" data-id="element-249">
            <div className="font-['Barlow_Condensed'] text-[12px] font-bold tracking-[0.2em] uppercase text-[#f5a623] mb-4" data-id="element-250">
              Elegí tu acceso
            </div>
            <h2 className="font-['Bebas_Neue'] text-[clamp(36px,6vw,56px)] leading-none text-[#f5f0e8] tracking-[0.02em] mb-2 uppercase" data-id="element-251">
              ELEGÍ TU <span className="text-[#f5a623]" data-id="element-252">PACK</span>
            </h2>
            <p className="text-[16px] text-[#888] mt-2" data-id="element-253">
              Precio de lanzamiento — puede subir en cualquier momento
            </p>
          </div>
          <PricingGrid selectedPack={selectedPack} setSelectedPack={setSelectedPack} packLinks={packLinks} data-id="element-254" />
        </div>
      </section>

      {/* 20. HOW IT WORKS */}
      <section className="py-10 md:py-16 px-5 bg-[#111111] border-y border-[#2a2a2a]" data-id="element-255">
        <div className="max-w-[780px] mx-auto" data-id="element-256">
          <div className="text-center mb-7" data-id="element-257">
            <div className="font-['Barlow_Condensed'] text-[12px] font-bold tracking-[0.2em] uppercase text-[#f5a623] mb-4" data-id="element-258">
              Simple y rápido
            </div>
            <h2 className="font-['Bebas_Neue'] text-[clamp(36px,6vw,56px)] leading-none text-[#f5f0e8] tracking-[0.02em] uppercase" data-id="element-259">
              ¿CÓMO <span className="text-[#f5a623]" data-id="element-260">RECIBÍS</span> EL MATERIAL?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-id="element-261">
            <div className="text-center p-5 md:p-7 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg" data-id="element-262">
              <div className="w-12 h-12 rounded-full bg-[rgba(245,166,35,0.1)] border-2 border-[#f5a623] text-[#f5a623] font-['Bebas_Neue'] text-[24px] flex items-center justify-center mx-auto mb-4 mt-1" data-id="element-263">
                1
              </div>
              <h3 className="font-['Barlow_Condensed'] text-[18px] font-bold text-[#f5f0e8] mb-2 uppercase tracking-[0.04em]" data-id="element-264">
                Elegís tu pack
              </h3>
              <p className="text-[14px] text-[#888]" data-id="element-265">
                Seleccionás la opción que mejor se adapta a tus objetivos e
                ingresás tu email.
              </p>
            </div>
            <div className="text-center p-5 md:p-7 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg" data-id="element-266">
              <div className="w-12 h-12 rounded-full bg-[rgba(245,166,35,0.1)] border-2 border-[#f5a623] text-[#f5a623] font-['Bebas_Neue'] text-[24px] flex items-center justify-center mx-auto mb-4 mt-1" data-id="element-267">
                2
              </div>
              <h3 className="font-['Barlow_Condensed'] text-[18px] font-bold text-[#f5f0e8] mb-2 uppercase tracking-[0.04em]" data-id="element-268">
                Pagás seguro
              </h3>
              <p className="text-[14px] text-[#888]" data-id="element-269">
                Mercado Pago, tarjeta de crédito/débito o transferencia
                bancaria. 100% seguro.
              </p>
            </div>
            <div className="text-center p-5 md:p-7 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg" data-id="element-270">
              <div className="w-12 h-12 rounded-full bg-[rgba(245,166,35,0.1)] border-2 border-[#f5a623] text-[#f5a623] font-['Bebas_Neue'] text-[24px] flex items-center justify-center mx-auto mb-4 mt-1" data-id="element-271">
                3
              </div>
              <h3 className="font-['Barlow_Condensed'] text-[18px] font-bold text-[#f5f0e8] mb-2 uppercase tracking-[0.04em]" data-id="element-272">
                Acceso inmediato
              </h3>
              <p className="text-[14px] text-[#888]" data-id="element-273">
                Recibís el link de descarga al instante en tu email. Desde el
                celular o la computadora.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 21. GUARANTEE */}
      <section className="py-10 md:py-16 px-5 bg-[#0a0a0a]" data-id="element-274">
        <div className="max-w-[780px] mx-auto" data-id="element-275">
          <div className="bg-[linear-gradient(135deg,rgba(34,197,94,0.06),rgba(34,197,94,0.02))] border-2 border-[rgba(34,197,94,0.3)] rounded-xl p-6 md:p-12 flex flex-col md:flex-row gap-5 md:gap-9 items-start" data-id="element-276">
            <div className="text-[48px] md:text-[64px] flex-shrink-0 leading-none" data-id="element-277">
              🛡️
            </div>
            <div data-id="element-278">
              <h2 className="font-['Bebas_Neue'] text-[32px] md:text-[40px] text-[#22c55e] tracking-[0.02em] mb-3 uppercase leading-none" data-id="element-279">
                Garantía de 15 Días — Sin Preguntas
              </h2>
              <p className="text-[16px] text-[#aaa] leading-[1.6]" data-id="element-280">
                Tomamos la confianza muy en serio. Probá el material durante{' '}
                <strong className="text-[#f5f0e8]" data-id="element-281">15 días completos</strong>.
                Si no cumple con lo que prometemos, te devolvemos el 100% de lo
                pagado —{' '}
                <strong className="text-[#f5f0e8]" data-id="element-282">
                  sin preguntas, sin burocracia.
                </strong>
                <br data-id="element-283" />
                <br data-id="element-284" />
                No hay riesgo de tu parte. O aprendés y empezás a generar
                ingresos, o te devolvemos el dinero. Así de simple.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 14. TESTIMONIALS */}
      <section className="py-10 md:py-16 px-5 bg-[#111111] border-y border-[#2a2a2a]" data-id="element-285">
        <div className="max-w-[780px] mx-auto" data-id="element-286">
          <div className="text-center mb-7" data-id="element-287">
            <div className="font-['Barlow_Condensed'] text-[12px] font-bold tracking-[0.2em] uppercase text-[#f5a623] mb-4" data-id="element-288">
              Resultados reales de alumnos
            </div>
            <h2 className="font-['Bebas_Neue'] text-[clamp(36px,6vw,56px)] leading-none text-[#f5f0e8] tracking-[0.02em] uppercase" data-id="element-289">
              LO QUE DICEN LOS QUE
              <br data-id="element-290" />
              <span className="text-[#f5a623]" data-id="element-291">YA LO HICIERON</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 md:gap-5" data-id="element-292">
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-5 md:p-7 relative" data-id="element-293">
              <div className="absolute top-4 right-5 font-['Bebas_Neue'] text-[72px] text-[rgba(245,166,35,0.1)] leading-none" data-id="element-294">
                "
              </div>
              <div className="text-[#f5a623] text-[14px] mb-3.5 tracking-[2px]" data-id="element-295">
                ★★★★★
              </div>
              <p className="text-[15px] text-[#ccc] leading-[1.6] mb-4.5 italic" data-id="element-296">
                "Siempre quise aprender un oficio que me diera libertad. Con
                este pack aprendí a abrir cerraduras en menos de dos semanas. Ya
                hice mis primeros trabajos y recuperé la inversión con el primer
                llamado."
              </p>
              <div className="flex items-center gap-3" data-id="element-297">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#c47d0e] to-[#f5a623] flex items-center justify-center font-['Bebas_Neue'] text-[18px] text-[#0a0a0a] flex-shrink-0 mt-1" data-id="element-298">
                  CM
                </div>
                <div data-id="element-299">
                  <div className="font-bold text-[14px] text-[#f5f0e8]" data-id="element-300">
                    Carlos M., 34 años
                  </div>
                  <div className="text-[12px] text-[#888]" data-id="element-301">Buenos Aires</div>
                </div>
              </div>
              <div className="mt-3 p-2.5 bg-[rgba(34,197,94,0.08)] border border-[rgba(34,197,94,0.2)] rounded text-[13px] text-[#22c55e] font-semibold" data-id="element-302">
                ✓ Recuperó la inversión en su primer trabajo
              </div>
            </div>

            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-7 relative" data-id="element-303">
              <div className="absolute top-4 right-5 font-['Bebas_Neue'] text-[72px] text-[rgba(245,166,35,0.1)] leading-none" data-id="element-304">
                "
              </div>
              <div className="text-[#f5a623] text-[14px] mb-3.5 tracking-[2px]" data-id="element-305">
                ★★★★★
              </div>
              <p className="text-[15px] text-[#ccc] leading-[1.6] mb-4.5 italic" data-id="element-306">
                "Estaba sin trabajo y necesitaba algo urgente. En pocos días ya
                entendía cómo funcionaban las cerraduras. Hoy hago 3 o 4
                trabajos por semana y genero más que en mi empleo anterior."
              </p>
              <div className="flex items-center gap-3" data-id="element-307">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#c47d0e] to-[#f5a623] flex items-center justify-center font-['Bebas_Neue'] text-[18px] text-[#0a0a0a] flex-shrink-0 mt-1" data-id="element-308">
                  RG
                </div>
                <div data-id="element-309">
                  <div className="font-bold text-[14px] text-[#f5f0e8]" data-id="element-310">
                    Roberto G., 41 años
                  </div>
                  <div className="text-[12px] text-[#888]" data-id="element-311">Córdoba</div>
                </div>
              </div>
              <div className="mt-3 p-2.5 bg-[rgba(34,197,94,0.08)] border border-[rgba(34,197,94,0.2)] rounded text-[13px] text-[#22c55e] font-semibold" data-id="element-312">
                ✓ 3–4 trabajos por semana, supera su sueldo anterior
              </div>
            </div>

            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-5 md:p-7 relative" data-id="element-313">
              <div className="absolute top-4 right-5 font-['Bebas_Neue'] text-[72px] text-[rgba(245,166,35,0.1)] leading-none" data-id="element-314">
                "
              </div>
              <div className="text-[#f5a623] text-[14px] mb-3.5 tracking-[2px]" data-id="element-315">
                ★★★★★
              </div>
              <p className="text-[15px] text-[#ccc] leading-[1.6] mb-4.5 italic" data-id="element-316">
                "No tenía idea de cerrajería. Los videos son muy claros y los
                manuales están bien explicados. En un mes ya estaba haciendo
                aperturas y cambios. La mejor inversión que hice."
              </p>
              <div className="flex items-center gap-3" data-id="element-317">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#c47d0e] to-[#f5a623] flex items-center justify-center font-['Bebas_Neue'] text-[18px] text-[#0a0a0a] flex-shrink-0 mt-1" data-id="element-318">
                  FL
                </div>
                <div data-id="element-319">
                  <div className="font-bold text-[14px] text-[#f5f0e8]" data-id="element-320">
                    Fernando L., 28 años
                  </div>
                  <div className="text-[12px] text-[#888]" data-id="element-321">Rosario</div>
                </div>
              </div>
              <div className="mt-3 p-2.5 bg-[rgba(34,197,94,0.08)] border border-[rgba(34,197,94,0.2)] rounded text-[13px] text-[#22c55e] font-semibold" data-id="element-322">
                ✓ Haciendo trabajos reales al mes de empezar
              </div>
            </div>

            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-5 md:p-7 relative" data-id="element-323">
              <div className="absolute top-4 right-5 font-['Bebas_Neue'] text-[72px] text-[rgba(245,166,35,0.1)] leading-none" data-id="element-324">
                "
              </div>
              <div className="text-[#f5a623] text-[14px] mb-3.5 tracking-[2px]" data-id="element-325">
                ★★★★★
              </div>
              <p className="text-[15px] text-[#ccc] leading-[1.6] mb-4.5 italic" data-id="element-326">
                "Tengo 45 años y pensé que era tarde para aprender algo nuevo.
                Este pack me demostró lo contrario. Hoy tengo clientes fijos y
                gano bien. Si yo pude, cualquiera puede."
              </p>
              <div className="flex items-center gap-3" data-id="element-327">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#c47d0e] to-[#f5a623] flex items-center justify-center font-['Bebas_Neue'] text-[18px] text-[#0a0a0a] flex-shrink-0 mt-1" data-id="element-328">
                  AR
                </div>
                <div data-id="element-329">
                  <div className="font-bold text-[14px] text-[#f5f0e8]" data-id="element-330">
                    Alejandro R., 45 años
                  </div>
                  <div className="text-[12px] text-[#888]" data-id="element-331">Tucumán</div>
                </div>
              </div>
              <div className="mt-3 p-2.5 bg-[rgba(34,197,94,0.08)] border border-[rgba(34,197,94,0.2)] rounded text-[13px] text-[#22c55e] font-semibold" data-id="element-332">
                ✓ Clientes fijos e ingresos constantes hoy
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 16. FAQ */}
      <section className="py-10 md:py-16 px-5 bg-[#0a0a0a]" data-id="element-333">
        <div className="max-w-[780px] mx-auto" data-id="element-334">
          <div className="text-center mb-7" data-id="element-335">
            <div className="font-['Barlow_Condensed'] text-[12px] font-bold tracking-[0.2em] uppercase text-[#f5a623] mb-4" data-id="element-336">
              Respondemos tus dudas
            </div>
            <h2 className="font-['Bebas_Neue'] text-[clamp(36px,6vw,56px)] leading-none text-[#f5f0e8] tracking-[0.02em] uppercase" data-id="element-337">
              PREGUNTAS <span className="text-[#f5a623]" data-id="element-338">FRECUENTES</span>
            </h2>
          </div>

          <div className="flex flex-col gap-3" data-id="element-339">
            {[{
            q: '¿Necesito experiencia previa?',
            a: 'No. El sistema está diseñado específicamente para personas sin ningún conocimiento previo en cerrajería. Empezás desde cero y avanzás de forma progresiva y ordenada.'
          }, {
            q: '¿En qué formato recibo el material?',
            a: 'Recibís archivos PDF descargables y links a videos. Podés acceder desde tu celular, tablet o computadora. Sin plataforma externa — el material es tuyo para siempre.'
          }, {
            q: '¿Cuánto tarda en llegar después de pagar?',
            a: 'El acceso es inmediato. En menos de 2 minutos de confirmar el pago recibís el email con todos los links de descarga.'
          }, {
            q: '¿Necesito herramientas especiales para empezar?',
            a: 'No de entrada. El Bonus "Maletín Inteligente del Cerrajero" te explica exactamente qué comprar, dónde conseguirlo en Argentina y cómo arrancar sin gastar de más. Podés practicar con lo básico mientras aprendés.'
          }, {
            q: '¿En cuánto tiempo puedo empezar a trabajar?',
            a: 'Depende del tiempo que le dediques. La mayoría de los alumnos logra hacer sus primeros trabajos entre la segunda y cuarta semana. Con 1 hora diaria de práctica los resultados son muy concretos.'
          }, {
            q: '¿Qué diferencia hay entre los dos packs?',
            a: 'El pack base cubre cerrajería domiciliaria completa: puertas, cerraduras, urgencias. El pack avanzado agrega un módulo completo de cerrajería automotriz (transponders, clonación, programación), que son los trabajos mejor pagos del mercado. Por $10.000 más accedés a una formación mucho más completa.'
          }, {
            q: 'No tengo tarjeta de crédito, ¿puedo pagar?',
            a: 'Sí. Aceptamos Mercado Pago (incluido saldo en cuenta), tarjeta de débito y transferencia bancaria. Todas las opciones están disponibles al momento de comprar.'
          }, {
            q: '¿Qué pasa si no me sirve?',
            a: 'Tenés 15 días de garantía total. Si por cualquier motivo el material no cumple tus expectativas, te devolvemos el 100% de lo pagado sin hacer preguntas. No hay riesgo.'
          }].map((faq, i) => <details key={i} className="group bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg overflow-hidden" data-id="element-340">
                <summary className="flex items-center justify-between cursor-pointer p-5 md:px-6 font-semibold text-[15px] text-[#f5f0e8] list-none select-none" data-id="element-341">
                  {faq.q}
                  <span className="text-[#f5a623] text-[22px] font-light flex-shrink-0 group-open:hidden" data-id="element-342">
                    +
                  </span>
                  <span className="text-[#f5a623] text-[22px] font-light flex-shrink-0 hidden group-open:block" data-id="element-343">
                    −
                  </span>
                </summary>
                <div className="px-5 md:px-6 pb-5 text-[15px] text-[#999] leading-[1.6] border-t border-[#2a2a2a] pt-4.5" data-id="element-344">
                  {faq.a}
                </div>
              </details>)}
          </div>
        </div>
      </section>

      {/* 15. BOTTOM PRICING */}
      <section className="py-10 md:py-16 px-5 bg-[#111111] border-y border-[#2a2a2a]" data-id="element-345">
        <div className="max-w-[780px] mx-auto" data-id="element-346">
          <div className="text-center mb-8" data-id="element-347">
            <div className="font-['Barlow_Condensed'] text-[12px] font-bold tracking-[0.2em] uppercase text-[#f5a623] mb-4" data-id="element-348">
              Último paso
            </div>
            <h2 className="font-['Bebas_Neue'] text-[clamp(36px,6vw,56px)] leading-none text-[#f5f0e8] tracking-[0.02em] mb-2 uppercase" data-id="element-349">
              ELEGÍ TU <span className="text-[#f5a623]" data-id="element-350">PACK</span>
            </h2>
            <p className="text-[16px] text-[#888] mt-2" data-id="element-351">
              Precio de lanzamiento — puede subir en cualquier momento
            </p>
          </div>
          <PricingGrid selectedPack={selectedPack} setSelectedPack={setSelectedPack} packLinks={packLinks} data-id="element-352" />
        </div>
      </section>

      {/* 19. FOOTER */}
      <footer className="bg-[#111111] border-t border-[#2a2a2a] py-7 px-5 text-center text-[13px] text-[#888888]" data-id="element-353">
        <p className="mb-2.5" data-id="element-354">
          © 2026 Mega Pack Maestro Cerrajero™ — Todos los derechos reservados
        </p>
        <div className="flex justify-center gap-2 mb-3" data-id="element-355">
          <a href="#" className="hover:text-[#f5a623] transition-colors" data-id="element-356">
            Términos y Condiciones
          </a>
          <span data-id="element-357">·</span>
          <a href="#" className="hover:text-[#f5a623] transition-colors" data-id="element-358">
            Política de Privacidad
          </a>
          <span data-id="element-359">·</span>
          <a href="#" className="hover:text-[#f5a623] transition-colors" data-id="element-360">
            Soporte
          </a>
        </div>
        <p className="text-[12px] text-[#555] max-w-2xl mx-auto" data-id="element-361">
          Los resultados mencionados son testimonios reales de alumnos. Los
          ingresos individuales dependen del esfuerzo, dedicación y mercado de
          cada persona. Este producto no garantiza resultados específicos.
        </p>
      </footer>
    </div>;
}